'use client';

import * as React from 'react';
import { motion } from 'framer-motion';

import { Container } from '@/components/utils/Container';
import { cn } from '@/lib/utils';
import { PublicHeroBackground } from './PublicHeroBackground';

export type PublicPageHeroProps = {
  /** Optional small uppercase eyebrow rendered above the headline. */
  eyebrow?: React.ReactNode;
  /**
   * Headline plain text. Use {@link headline}/{@link accent} together to render
   * "<plain> <accent>" with the accent wrapped in the burgundy block treatment.
   */
  headline: React.ReactNode;
  /** Optional accent text rendered after the headline with the burgundy underline block. */
  accent?: React.ReactNode;
  /** Body paragraph below the headline. */
  description?: React.ReactNode;
  /** Slot rendered below the description (CTA buttons, secondary content). */
  children?: React.ReactNode;
  /** Match the page width to the surrounding container. Defaults to max-w-5xl. */
  containerClassName?: string;
  /** Adjust outer section padding bottom. Defaults to "pb-6". */
  bottomSpacing?: string;
  /** Override headline alignment. Defaults to "center". */
  align?: 'center' | 'start';
  className?: string;
};

/**
 * Shared marketing/public-page hero. Encapsulates the gradient + animated orbs
 * background, the centered headline with burgundy accent block, and an
 * optional eyebrow + description, so individual pages no longer reinvent the
 * pattern at slightly different sizes.
 */
export function PublicPageHero({
  eyebrow,
  headline,
  accent,
  description,
  children,
  containerClassName,
  bottomSpacing = 'pb-6',
  align = 'center',
  className,
}: PublicPageHeroProps) {
  const isCenter = align === 'center';

  return (
    <section
      className={cn(
        'relative overflow-hidden -mt-28 pt-[76px]',
        bottomSpacing,
        className,
      )}
    >
      <PublicHeroBackground />

      <Container className={cn('relative z-10 max-w-5xl', containerClassName)}>
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
          className={cn(
            'space-y-5',
            isCenter && 'text-center',
          )}
        >
          {eyebrow ? (
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
              {eyebrow}
            </p>
          ) : null}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-[1.1] tracking-tight text-foreground">
            {headline}
            {accent ? (
              <>
                {' '}
                <span className="relative inline-block">
                  <span className="relative z-10 bg-primary/15 px-2 py-1 border-l-[3px] border-primary">
                    {accent}
                  </span>
                </span>
              </>
            ) : null}
          </h1>
          {description ? (
            <p
              className={cn(
                'text-base sm:text-lg leading-relaxed text-muted-foreground',
                isCenter ? 'mx-auto max-w-2xl' : 'max-w-2xl',
              )}
            >
              {description}
            </p>
          ) : null}
          {children ? (
            <div className={cn(isCenter && 'flex flex-col items-center')}>{children}</div>
          ) : null}
        </motion.div>
      </Container>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
    </section>
  );
}
